import type { Me } from './api';
import { readPref, writePref } from './prefs';

/**
 * How the instance list is laid out.
 *
 * `cozy` is the stacked-button list a customer sees; `dense` is the compact
 * row list with a filter, alphabetical headers and a jump rail, which only
 * earns its chrome once there are enough rows to get lost in.
 */
export type InstanceView = 'cozy' | 'dense';

const STORAGE_KEY = 'x5.instanceView';

/**
 * The SafetyNet team's own organization. Its members get the dense view and the
 * view switcher however many rows the account happens to hold, so an internal
 * user never lands on the customer layout by accident.
 *
 * Matched on the lowercased name rather than the id because the row is spelled
 * `Xiltrix North America` while the brand is `XiltriX` everywhere else, and a
 * miss here only costs the default view — the count rule below still applies.
 */
const INTERNAL_ORGS = new Set(['xiltrix north america']);

/**
 * Below this, the filter box and jump rail are chrome around nothing: the
 * median customer organization has one instance, SafetyNet's has over a hundred.
 */
const DENSE_THRESHOLD = 8;

export function isInternalOrg(me: Me | null): boolean {
  const name = me?.organization?.name?.trim().toLowerCase();
  return name !== undefined && INTERNAL_ORGS.has(name);
}

/** Whether this caller is offered the Cozy/Dense switch at all. */
export function canSwitchView(me: Me | null, instanceCount: number): boolean {
  return isInternalOrg(me) || instanceCount >= DENSE_THRESHOLD;
}

/** The view a caller gets before they have ever chosen one. */
export function defaultView(me: Me | null, instanceCount: number): InstanceView {
  return canSwitchView(me, instanceCount) ? 'dense' : 'cozy';
}

const VIEWS = ['cozy', 'dense'] as const;

export function readStoredView(): InstanceView | null {
  return readPref(STORAGE_KEY, VIEWS);
}

export function storeView(view: InstanceView): void {
  writePref(STORAGE_KEY, view);
}
